import { ChangeEvent, FC, useEffect, useState, useRef } from 'react';
import {
    Box,
    Button,
    Checkbox,
    Flex,
    Input,
    Select,
    Text,
    VStack,
    useToast,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    useDisclosure,
} from '@chakra-ui/react';

import { Dictionary } from 'lodash';

import {
    DEFAULT_USER_GROUP,
    DISABLED_FIELDS,
    HEADER_USER,
    SELECT_LANGUAGES,
    THEADER_USER,
    USER_FIELDS,
    USER_FORM_SKIPPED_FIELD,
} from 'pages/UserManagement/const';
import {
    ICompanyGroups,
    IInternationalCompany,
    ISelectProps,
    IUserGroup,
    TCompanyWithCompanyGroupsDict,
    TOptionalUser,
    TUser,
    TUSER,
} from 'pages/UserManagement/types';
import { entries, intentionalyFloatingPromiseReturn } from 'utils/wrappers';
import {
    useAddUserMutation,
    useGetCompanyGroupQuery,
    useGetInternationalCompanyQuery,
    useGetUsersGroupQuery,
    useResetUserPasswordMutation,
    useUpdateUserMutation,
} from 'core/API/usersApi';

import { isEmail } from 'utils/strings';
import AMultiSelect, { MultiSelectOption } from 'components/atoms/AMultiSelect';
import { isFetchBaseQueryError } from 'utils/erros';
import { linkStyle } from 'shared/styles';
import { useTranslation } from 'helpers/languages/translations';
import { IinternationalCompanies } from 'types';

type TGenericItem = {
    id: number;
    name: string;
    [key: string]: any;
};

const MUserEdit: FC<{
    isInternational: boolean;
    isAdd: boolean;
    values?: TUser;
    isInternationalUser?: boolean;
    internationalCompanyGroups: TGenericItem[];
    internationalCompanies: IinternationalCompanies[];
    companyGroupsByCompany: TCompanyWithCompanyGroupsDict;
    internationalCompaniesById: Dictionary<IinternationalCompanies>;
    readonly?: boolean;
    onClose: () => void;
}> = ({
    isInternational,
    isAdd,
    values,
    isInternationalUser,
    internationalCompanyGroups,
    internationalCompanies,
    companyGroupsByCompany,
    internationalCompaniesById,
    readonly,
    onClose,
}) => {
    const toast = useToast();
    const translate = useTranslation();
    const [updateUserMutation, { isLoading }] = useUpdateUserMutation();
    const [addUserMutation, { isLoading: isAddUserLoading }] = useAddUserMutation();
    const [resetPasswordMutation, { isLoading: isResetPasswordLoading }] = useResetUserPasswordMutation();
    const [companyList, setCompanyList] = useState<TGenericItem[]>([]);

    const {
        data: userGroupsData,
        isLoading: isGetUserGroupLoading,
        isError: isGetUserGroupError,
    } = useGetUsersGroupQuery();

    const {
        data: userCompanyGroupsData,
        isLoading: isGetCompanyGroupLoading,
        isError: isGetCompanyGroupError,
    } = useGetCompanyGroupQuery();

    const {
        data: userInternationalCompanyData,
        isLoading: isGetInternationalCompanyLoading,
        isError: isGetInternationalCompanyError,
    } = useGetInternationalCompanyQuery();

    const { isOpen: isResetPwdModalOpen, onOpen: onOpenResetPwdModal, onClose: onCloseResetPwdModal } = useDisclosure();
    const finalRef = useRef(null);
    const [userGroups, setUserGroupValues] = useState<IUserGroup[]>([]);
    const [companyGroups, setCompanyGroupValues] = useState<ICompanyGroups[]>([]);
    const [IntUserCompanyGroups, setIntUserCompanyGroups] = useState<TGenericItem[]>([]);
    const [internationCompany, setInternationCompany] = useState<IInternationalCompany[]>([]);
    const [fieldsValues, setFieldsValues] = useState<TUser>(values as TUser);
    const [initialFieldsValues, setInitialFieldsValues] = useState<TOptionalUser>(values as TOptionalUser);
    const [isUsernameValid, setIsUsernameValid] = useState<boolean>(false);
    const [isUserGroupValid, setIsUserGroupValid] = useState<boolean>(false);
    const [hiddenInput, setHiddenInput] = useState<string[]>(USER_FORM_SKIPPED_FIELD);
    const [selectedTools, setSelectedTools] = useState<MultiSelectOption[]>([]);
    const [options, setOptions] = useState<MultiSelectOption[]>([]);

    useEffect(() => {
        if (!isAdd && values) {
            setIsUsernameValid(isEmail(values?.username) !== null);
            setFieldsValues(values);
            setInitialFieldsValues(values);
        }

        if (isAdd) {
            const userGroup: IUserGroup = DEFAULT_USER_GROUP;

            setFieldsValues({
                ...fieldsValues,
                is_active: true,
                groups: [userGroup],
            });

            setSelectedTools([userGroup]);
        }
    }, [values]);

    useEffect(() => {
        if (fieldsValues?.groups?.length) {
            setIsUserGroupValid(true);
        } else {
            setIsUserGroupValid(false);
        }
    }, [fieldsValues]);

    const updateCompanyGroups = async () => {
        if (Object.keys(companyGroupsByCompany).length) {
            const companyGroupsList =
                (fieldsValues.company && companyGroupsByCompany[fieldsValues.company]?.company_groups) || {};

            const companyGroupsArray = Object.entries(companyGroupsList).map(([key, value]) => ({
                id: Number(key),
                name: value.name,
            }));

            setIntUserCompanyGroups(companyGroupsArray);
        }
    };

    useEffect(() => {
        updateCompanyGroups();
        if (!fieldsValues.company) setFieldsValues((prev) => ({ ...prev, company_group: undefined }));
    }, [fieldsValues.international_company, fieldsValues.company]);

    useEffect(() => {
        if (!isGetUserGroupLoading && !isGetUserGroupError && userGroupsData) {
            setUserGroupValues(userGroupsData.groups);
            setOptions(
                userGroupsData.groups.map((item) => {
                    return { id: item.id, name: item.name };
                }),
            );

            if (!isAdd) {
                const selectedToolsData = userGroups
                    .filter((tool) => values && (values.groups as IUserGroup[]).find((group) => group.id === tool.id))
                    .map((tool) => tool);

                setSelectedTools(selectedToolsData);
            }
        }
    }, [isGetUserGroupLoading, isGetUserGroupError, userGroupsData, userGroups]);

    useEffect(() => {
        if (fieldsValues.international_company && internationalCompaniesById[fieldsValues.international_company]) {
            const newCompanyList = internationalCompaniesById[fieldsValues.international_company].companies;
            const companyFound = newCompanyList.find((company) => company.id === fieldsValues.company);
            if (!companyFound) setFieldsValues((prev) => ({ ...prev, company: '', company_group: undefined }));
            setCompanyList(newCompanyList);
        } else if (Object.keys(companyGroupsByCompany).length) {
            const formattedCompanies = Object.entries(companyGroupsByCompany).map(([key, value]) => ({
                id: Number(key),
                name: value.name,
            }));
            setCompanyList(formattedCompanies);
        }
    }, [fieldsValues.international_company, internationalCompaniesById]);

    useEffect(() => {
        if (userCompanyGroupsData && !isGetCompanyGroupLoading && !isGetCompanyGroupError) {
            setCompanyGroupValues(userCompanyGroupsData.companyGroup);
        }
    }, [isGetCompanyGroupLoading, isGetCompanyGroupError]);

    useEffect(() => {
        if (userInternationalCompanyData && !isGetInternationalCompanyError && !isGetInternationalCompanyLoading) {
            setInternationCompany(userInternationalCompanyData.internationalCompanies);
        }
        if (!userInternationalCompanyData && !isGetInternationalCompanyLoading && isGetInternationalCompanyError) {
            setHiddenInput((prev) => [...prev, 'international_company']);
        }
    }, [isGetInternationalCompanyError, isGetInternationalCompanyLoading]);

    useEffect(() => {
        if (!isInternationalUser) {
            setHiddenInput((prev) => [...prev, 'company', 'international_company_group']);
        }
    }, [isInternationalUser]);

    useEffect(() => {
        if (isInternationalUser && isAdd) {
            setHiddenInput((prev) => [...prev, 'international_company_group']);
        }
    }, [isAdd]);

    const onChangeMultiSelect = (selected: MultiSelectOption[]) => {
        setFieldsValues({
            ...fieldsValues,
            groups: [...(selected as IUserGroup[])],
        });
    };

    const handleChangeInput = (event: ChangeEvent<HTMLInputElement | HTMLSelectElement>, field: TUSER) => {
        if (event.target.value !== null) {
            if (field === 'company_group') {
                setFieldsValues({ ...fieldsValues, [field]: Number(event.target.value) });
            } else {
                setFieldsValues({ ...fieldsValues, [field]: event.target.value });
            }

            if (field === 'username') {
                setIsUsernameValid(isEmail(event.target.value) !== null);
            }
            if (field === 'international_company' && !event.target.value)
                setFieldsValues({ ...fieldsValues, [field]: null });
        } else {
            if (field === 'international_company') setFieldsValues({ ...fieldsValues, [field]: null });
        }
    };

    const handleCheckRow = (e: ChangeEvent<HTMLInputElement>, isActive: boolean) => {
        setFieldsValues({ ...fieldsValues, is_active: !isActive });
    };

    const renderSelectInput = <T extends ISelectProps>(
        field: keyof typeof HEADER_USER,
        data: T[],
        value: any,
        isDisabled = false,
    ) => {
        return (
            <Flex w="100%" justifyContent="space-between" key={field}>
                <Text mr="24px">{translate(HEADER_USER[field])}</Text>
                <Select
                    value={value}
                    onChange={(e) => handleChangeInput(e, field)}
                    placeholder={
                        isDisabled ? ' ' : `${translate('users.edit.select')} ${translate(HEADER_USER[field])}`
                    }
                    w="60%"
                    bg="white"
                    isDisabled={isDisabled}
                >
                    {data && data.map((group) => <option value={group.id}>{group.name}</option>)}
                </Select>
            </Flex>
        );
    };

    const renderSelectInputUserGroup = <T extends ISelectProps>(
        field: keyof typeof HEADER_USER,
        data: T[],
        value: IUserGroup[],
    ) => {
        return (
            <Flex w="100%" justifyContent="space-between" key={field}>
                <Text mr="24px">{translate(HEADER_USER[field])}*</Text>
                <VStack w="60%">
                    <AMultiSelect
                        disabled={readonly}
                        selectedOptions={selectedTools}
                        options={options}
                        onChange={onChangeMultiSelect}
                    />
                    {!isUserGroupValid && data.length && (
                        <Text fontSize="sm">{translate('users.edit.error.user_group')}</Text>
                    )}
                </VStack>
            </Flex>
        );
    };

    const renderInput = (field: THEADER_USER, fieldsValuesInput: TUser, isDisabled = false) => {
        let data: any[] = [];

        switch (field) {
            case 'language':
                data = SELECT_LANGUAGES;
                break;
            case 'international_company_group':
                data = internationalCompanyGroups;
                break;
            case 'groups':
                data = userGroups;
                if (fieldsValuesInput) {
                    return renderSelectInputUserGroup(field, data, fieldsValuesInput.groups as IUserGroup[]);
                }
                break;
            case 'company':
                data = companyList;
                break;
            case 'company_group':
                data = isInternationalUser ? IntUserCompanyGroups : companyGroups;
                break;
            case 'international_company':
                data = isInternationalUser ? internationalCompanies : internationCompany;
                break;
            case 'has_notification_pdf_active':
            case 'is_active':
            case 'is_staff':
            case 'is_superuser':
                return (
                    <Flex w="100%" justifyContent="space-between" key={field}>
                        <Text mr="24px">{translate(HEADER_USER[field])}</Text>
                        <Checkbox
                            disabled={readonly}
                            onChange={(e) => handleCheckRow(e, fieldsValuesInput[field])}
                            isChecked={fieldsValuesInput ? fieldsValuesInput[field] : false}
                        />
                    </Flex>
                );
            case 'username':
                return (
                    <Flex w="100%" justifyContent="space-between" key={field}>
                        <Text mr="24px">{translate(HEADER_USER[field])}*</Text>
                        <VStack w="60%">
                            <Input
                                disabled={!isAdd || readonly}
                                isInvalid={!isUsernameValid}
                                errorBorderColor="crimson"
                                onChange={(e) => handleChangeInput(e, field)}
                                bg="white"
                                value={fieldsValuesInput ? fieldsValuesInput[field] : ''}
                            />
                            {!isUsernameValid && fieldsValuesInput[field] && (
                                <Text fontSize="sm">{translate('users.edit.error.email')}</Text>
                            )}
                        </VStack>
                    </Flex>
                );
            default:
                return (
                    <Flex w="100%" justifyContent="space-between" key={field}>
                        <Text mr="24px">{translate(HEADER_USER[field])}</Text>
                        <Input
                            onChange={(e) => handleChangeInput(e, field)}
                            disabled={readonly}
                            w="60%"
                            bg="white"
                            value={fieldsValuesInput ? fieldsValuesInput[field] : ''}
                        />
                    </Flex>
                );
        }

        return renderSelectInput(
            field,
            data,
            fieldsValuesInput ? fieldsValuesInput[field] : '',
            isDisabled || readonly,
        );
    };

    const openResetPwdModal = () => {
        onOpenResetPwdModal();
    };

    const resetPasswordTrigger = async () => {
        try {
            const res = await resetPasswordMutation([fieldsValues.id]);
            if ('error' in res) {
                const errorObj = res.error;
                if (isFetchBaseQueryError(errorObj)) {
                    const errorMsg: string =
                        'message' in (errorObj.data as any)
                            ? (errorObj.data as any).message
                            : JSON.stringify(errorObj.data);
                    throw new Error(errorMsg);
                }
                throw new Error('Error reset password');
            }

            onClose();
            onCloseResetPwdModal();

            toast({
                title: `Succesfully reset password`,
                status: 'success',
                isClosable: true,
            });
        } catch (error) {
            toast({
                title: (error as Error).message,
                status: 'error',
                isClosable: true,
            });
        }
    };

    const renderConfirmResetPasswordModal = () => {
        return (
            <Modal finalFocusRef={finalRef} isOpen={isResetPwdModalOpen} onClose={onClose}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>{translate('users.edit.modal.reset_password.title')}</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <Text fontWeight="bold" mb="1rem">
                            {translate('users.edit.modal.reset_password.body')} {fieldsValues.username}
                        </Text>
                    </ModalBody>

                    <ModalFooter>
                        <Button
                            onClick={resetPasswordTrigger}
                            colorScheme="red"
                            variant="solid"
                            mr={3}
                            isLoading={isResetPasswordLoading}
                        >
                            {translate('users.edit.modal.reset_password.ok')}
                        </Button>
                        <Button colorScheme="blue" mr={3} onClick={onClose} isLoading={isResetPasswordLoading}>
                            {translate('users.edit.modal.cancel')}
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        );
    };

    const renderUserForm = () => {
        const form = USER_FIELDS.filter((field) => !hiddenInput.includes(field)).map((field) => {
            return renderInput(field as THEADER_USER, fieldsValues, DISABLED_FIELDS.includes(field));
        });

        if (!isAdd && !isInternationalUser && !readonly) {
            form.push(
                <Flex w="100%" justifyContent="end">
                    <Box {...linkStyle} onClick={openResetPwdModal}>
                        {translate('users.edit.modal.reset_password.title')}
                    </Box>
                    {renderConfirmResetPasswordModal()}
                </Flex>,
            );
        }

        return form;
    };

    const createUpdateUserObject = (fieldsValuesCreate: TOptionalUser) => {
        const newFielValues: TOptionalUser = {
            ...fieldsValuesCreate,
            groups: fieldsValuesCreate.groups && (fieldsValuesCreate.groups as IUserGroup[]).map((group) => group.id),
        };

        if (!isInternational) {
            delete newFielValues.international_company;
        }
        return newFielValues;
    };

    const createUserData = (fieldsValuesCreate: TOptionalUser) => {
        const newFieldsValue: TOptionalUser = {
            ...fieldsValuesCreate,
            groups:
                fieldsValuesCreate.groups && fieldsValuesCreate.groups.map((userGroup) => (userGroup as IUserGroup).id),
        };

        entries(newFieldsValue).forEach(([key, value]) => {
            switch (key) {
                case 'company_group':
                    if (newFieldsValue.company_group === 0) {
                        delete newFieldsValue[key];
                    }
                    break;
                case 'groups':
                    if (
                        newFieldsValue.groups &&
                        (newFieldsValue.groups[0] === 0 ||
                            !newFieldsValue.groups[0] ||
                            newFieldsValue.groups.length === 0)
                    ) {
                        delete newFieldsValue[key];
                    }
                    break;
                default:
                    if (value === '') {
                        delete newFieldsValue[key];
                    }
                    break;
            }
        });

        return newFieldsValue;
    };

    const handleUpdateUser = async () => {
        try {
            if (fieldsValues) {
                const missingFields: string[] = [];
                if (!fieldsValues.username) missingFields.push('email');
                if (!fieldsValues.first_name) missingFields.push('first_name');
                if (!fieldsValues.last_name) missingFields.push('last_name');
                if (!fieldsValues.language) missingFields.push('language');
                if (!fieldsValues.company && isInternationalUser) missingFields.push('company');

                if (missingFields.length > 0) {
                    toast({
                        title: `${translate('users.modal.error.missing_fields')}: ${missingFields
                            .map((field) => translate(`users.${field}`))
                            .join(', ')}`,
                        status: 'error',
                        isClosable: true,
                    });
                    return;
                }

                const fieldsToSubmit = entries(fieldsValues).reduce((acc: any, [key, value]) => {
                    if (initialFieldsValues[key as keyof TOptionalUser] !== value) {
                        acc[key] = value;
                    }
                    return acc;
                }, {} as TOptionalUser);

                const res = isAdd
                    ? await addUserMutation(createUserData(fieldsToSubmit))
                    : await updateUserMutation({
                          id: String(fieldsValues.id),
                          data: createUpdateUserObject(fieldsToSubmit),
                      });

                if ('error' in res) {
                    const errorObj = res.error;
                    if (isFetchBaseQueryError(errorObj)) {
                        const errorMsg: string =
                            'message' in (errorObj.data as any)
                                ? (errorObj.data as any).message
                                : 'uamError' in (errorObj.data as any)
                                ? (errorObj?.data as any)?.uamError
                                : 'error' in (errorObj.data as any) && (errorObj.data as any).error?.detail
                                ? (errorObj.data as any).error.detail
                                : JSON.stringify(errorObj.data);
                        throw new Error(errorMsg);
                    }
                    throw new Error('error.generic');
                }

                if (res?.data?.users[0]?.failed_users?.length > 0) {
                    throw new Error(res?.data?.users[0]?.failed_users[0]?.error);
                }
            }

            toast({
                title: isAdd ? translate('user.add.message.success') : translate('user.edit.message.success'),
                status: 'success',
                isClosable: true,
            });

            onClose();
        } catch (e) {
            toast({
                title: translate(`${e}`),
                status: 'error',
                isClosable: true,
            });
        }
    };

    return (
        <Box>
            <Flex w="100%" alignItems="flex-start" flexDir="column" px="16px">
                <VStack w="100%" alignItems="flex-start">
                    {renderUserForm()}
                </VStack>
            </Flex>
            <Flex marginTop={8} flex="1" justifyContent="flex-end">
                <Button onClick={onClose} size="sm" variant="outline" mr={3}>
                    {translate('users.edit.modal.cancel')}
                </Button>
                {!readonly && (
                    <Button
                        isDisabled={!isUsernameValid || !isUserGroupValid}
                        onClick={intentionalyFloatingPromiseReturn(handleUpdateUser)}
                        size="sm"
                        colorScheme="blue"
                        bg="blueMain"
                        isLoading={isLoading || isAddUserLoading || isResetPasswordLoading}
                    >
                        {translate('users.edit.modal.apply')}
                    </Button>
                )}
            </Flex>
        </Box>
    );
};

export default MUserEdit;
